import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useGetCopilotChat } from 'shared/copilot/api';
import { type CopilotAgentName, resolveChatAgentName } from 'shared/copilot/copilotSurfaces';
interface UseDrawerChatAgentArgs {
  currentChatId: string | null;
  /** Chat restored at mount or picked from history whose agent is not yet checked. */
  chatToResolve: string | null;
  clearChatToResolve: () => void;
  agentName: CopilotAgentName;
  preferredAgentName: CopilotAgentName;
  setAgentName: (agentName: CopilotAgentName) => void;
  loadChat: (chatId: string) => void;
}

/**
 * Binds the drawer to the agent a restored or picked chat was created on, then reloads the chat on
 * it (its ADK session lives on that agent's Engine). New chats bind to the preferred agent.
 */
export const useDrawerChatAgent = t0 => {
  const $ = _c(21);
  const {
    currentChatId,
    chatToResolve,
    clearChatToResolve,
    agentName,
    preferredAgentName,
    setAgentName,
    loadChat
  } = t0;
  const {
    currentClient
  } = useCurrentClient();
  const {
    currentUser
  } = useAuth();
  const {
    data: chat,
    isError
  } = useGetCopilotChat(currentClient.id, chatToResolve, currentUser?.id);
  const pendingReloadRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== agentName || $[1] !== chat || $[2] !== chatToResolve || $[3] !== clearChatToResolve || $[4] !== currentChatId || $[5] !== isError || $[6] !== setAgentName) {
    t1 = () => {
      if (chatToResolve == null) {
        return;
      }
      if (chatToResolve !== currentChatId) {
        clearChatToResolve();
        return;
      }
      if (chat == null && !isError) {
        return;
      }
      clearChatToResolve();
      if (chat == null) {
        return;
      }
      const chatAgentName = resolveChatAgentName("drawer", chat.agentName);
      if (chatAgentName === agentName) {
        return;
      }
      pendingReloadRef.current = {
        chatId: chatToResolve,
        agentName: chatAgentName
      };
      setAgentName(chatAgentName);
    };
    t2 = [agentName, chat, chatToResolve, clearChatToResolve, currentChatId, isError, setAgentName];
    $[0] = agentName;
    $[1] = chat;
    $[2] = chatToResolve;
    $[3] = clearChatToResolve;
    $[4] = currentChatId;
    $[5] = isError;
    $[6] = setAgentName;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] !== agentName || $[10] !== currentChatId || $[11] !== loadChat) {
    t3 = () => {
      const pending = pendingReloadRef.current;
      if (pending?.agentName !== agentName) {
        return;
      }
      pendingReloadRef.current = null;
      if (pending.chatId === currentChatId) {
        loadChat(pending.chatId);
      }
    };
    t4 = [agentName, currentChatId, loadChat];
    $[9] = agentName;
    $[10] = currentChatId;
    $[11] = loadChat;
    $[12] = t3;
    $[13] = t4;
  } else {
    t3 = $[12];
    t4 = $[13];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[14] !== agentName || $[15] !== chatToResolve || $[16] !== currentChatId || $[17] !== preferredAgentName || $[18] !== setAgentName) {
    t5 = () => {
      if (currentChatId == null && chatToResolve == null && agentName !== preferredAgentName) {
        setAgentName(preferredAgentName);
      }
    };
    t6 = [agentName, chatToResolve, currentChatId, preferredAgentName, setAgentName];
    $[14] = agentName;
    $[15] = chatToResolve;
    $[16] = currentChatId;
    $[17] = preferredAgentName;
    $[18] = setAgentName;
    $[19] = t5;
    $[20] = t6;
  } else {
    t5 = $[19];
    t6 = $[20];
  }
  useEffect(t5, t6);
};